import { onBeforeUnmount } from 'vue'

import { useFeatureFlags } from '@/utils/useFeatureFlags'

import { buildHestiaIframeAuthUrl, getHestiaUrl } from './UseHestiaIframe'

const TEARDOWN_DELAY_MS = 10_000

/**
 * Loads Hestia's iframe-auth page in a hidden iframe after the browser goes
 * idle to warm the browser cache with JS bundles, fonts, and API responses.
 * The `?preload` query param tells Hestia to skip the HESTIA_IFRAME_READY
 * postMessage, avoiding interference with real useHestiaIframe instances.
 */
export const useHestiaPreload = (): void => {
  const featureFlags = useFeatureFlags()
  if (!featureFlags.isEnabled('hermes.hestiaPreload')) {
    return
  }

  let iframe: HTMLIFrameElement | null = null
  let teardownId: number | null = null

  const cleanup = (): void => {
    if (teardownId != null) clearTimeout(teardownId)
    iframe?.remove()
    iframe = null
    teardownId = null
  }

  const start = (): void => {
    const src = `${buildHestiaIframeAuthUrl({ hestiaUrl: getHestiaUrl() })}?preload`

    iframe = document.createElement('iframe')
    iframe.src = src
    iframe.setAttribute('aria-hidden', 'true')
    iframe.setAttribute('tabindex', '-1')
    iframe.style.cssText =
      'position:fixed;width:0;height:0;border:none;opacity:0;pointer-events:none;'

    document.body.appendChild(iframe)
    teardownId = window.setTimeout(cleanup, TEARDOWN_DELAY_MS)
  }

  const idleId =
    typeof window.requestIdleCallback === 'function'
      ? window.requestIdleCallback(start, { timeout: 5000 })
      : window.setTimeout(start, 3000)

  onBeforeUnmount(() => {
    if (typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(idleId)
    } else {
      clearTimeout(idleId)
    }
    cleanup()
  })
}
